import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { HOME_ROUTE_FOR_ROLE } from '../../routes/roleHome';
import './AuthForm.css';

interface LocationState {
  from?: { pathname: string };
}

export function LoginPage() {
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Already signed in — don't show a login form.
  if (!loading && user) {
    return <Navigate to={HOME_ROUTE_FOR_ROLE[user.role]} replace />;
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setFormError(null);
    try {
      const signedIn = await login({ email, password });
      // Return them where they were headed, else their role's home.
      const from = (location.state as LocationState | null)?.from?.pathname;
      navigate(from ?? HOME_ROUTE_FOR_ROLE[signedIn.role], { replace: true });
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Sign-in failed.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-form">
      <div className="auth-form__card card">
        <h1 className="auth-form__title">Sign in</h1>
        <p className="auth-form__subtitle">Welcome back to Voleto.</p>

        <form onSubmit={(e) => void handleSubmit(e)} noValidate>
          {formError && (
            <p className="alert alert--error auth-form__error" role="alert">
              {formError}
            </p>
          )}

          <div className="field">
            <label className="field__label" htmlFor="login-email">
              Email
            </label>
            <input
              id="login-email"
              className="field__input"
              type="email"
              name="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="login-password">
              Password
            </label>
            <input
              id="login-password"
              className="field__input"
              type="password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <button
            type="submit"
            className="button button--primary button--block"
            disabled={submitting}
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p className="auth-form__switch">
          No account? <Link to="/register">Create one</Link>
        </p>
      </div>
    </div>
  );
}
